import { fetchTransaction } from '~/api/transaction';
import { backendApiGateway } from '~/client/backend';
import { Counterpart } from '~/types/counterparts';
import { Document } from '~/types/document';
import { Flow } from '~/types/flow';
import {
  BookkeepingAccount,
  TransactionRegistrationProposal,
  BookkeepingEntrySource,
} from '~/types/general-bookkeeping';
import { Money } from '~/types/money';
import { Transaction } from '~/types/transaction';
import { isDefined } from '~/types/typeGuards';
import { buildJsonAPIQueryParams, jsonAPIFilterBuilder } from '~/utils/jsonapi-query';

export interface TransactionRegistrationProposalEntryResponse {
  bookkeepingAccount: BookkeepingAccount | null;
  amount: Money;
  counterpart?: Counterpart;
  document?: Document;
  flow?: Flow;
  source: BookkeepingEntrySource;
}

interface TransactionRegistrationProposalResponse {
  transactionIds: Transaction['id'][];
  description: string;
  date: Date;
  recommendedFlowId: string | null;
  entries: TransactionRegistrationProposalEntryResponse[];
}

export const fetchTransactionRegistrationProposals = async (transactions: Transaction[]) => {
  const filterBuilder = jsonAPIFilterBuilder();
  filterBuilder.in(
    'id',
    transactions.map((t) => t.id),
  );

  const params = buildJsonAPIQueryParams(filterBuilder);

  const { data } = await backendApiGateway.get<TransactionRegistrationProposalResponse[]>(
    '/api/v1/bookkeeping-transactions/proposals',
    {
      params,
    },
  );

  const transactionMap = new Map<Transaction['id'], Transaction>(transactions.map((t) => [t.id, t]));
  const allProposalIds = new Set<Transaction['id']>();
  data.forEach((item) => item.transactionIds.forEach((id) => allProposalIds.add(id)));

  const missingIds = [...allProposalIds].filter((id) => !transactionMap.has(id));
  if (missingIds.length > 0) {
    const fetchedTransactions = await Promise.all(missingIds.map((id) => fetchTransaction(id)));
    fetchedTransactions.forEach((t) => {
      if (!isDefined(t.bookkeepingTransaction)) transactionMap.set(t.id, t);
    });
  }

  const responseMap = new Map<Transaction['id'], TransactionRegistrationProposalResponse>();

  data.forEach((item) => {
    item.transactionIds.forEach((transactionId) => {
      if (!responseMap.has(transactionId)) {
        responseMap.set(transactionId, item);
      }
    });
  });

  return transactions.map((transaction) => {
    const response = responseMap.get(transaction.id);
    const transactions = response?.transactionIds.map((id) => transactionMap.get(id)) || null;

    if (response && transactions?.every(isDefined)) {
      return {
        transactionId: transaction.id,
        registrationProposal: {
          description: response.description,
          date: new Date(response.date),
          entries: response.entries.map((entry) => ({
            bookkeepingAccount: entry.bookkeepingAccount,
            amount: entry.amount,
            counterpart: entry.counterpart,
            document: entry.document,
            flow: entry.flow,
            source: entry.source,
          })),
          transactions,
          recommendedFlowId: response.recommendedFlowId,
        } satisfies TransactionRegistrationProposal,
      };
    }

    return {
      transactionId: transaction.id,
    };
  });
};
